import React, { useState, useCallback, useEffect, useRef } from 'react';
import { TextField, InputAdornment, Tooltip, useTheme, alpha, IconButton } from '@mui/material';
import { Search, X } from 'lucide-react';

interface SearchInputProps {
  /** Placeholder text for the search input */
  placeholder?: string;
  /** Tooltip text to show on hover */
  tooltipText?: string;
  /** Callback triggered after debounce delay */
  onSearch: (searchTerm: string) => void;
  /** External search value (for resetting from parent) */
  searchValue?: string;
  /** Width of the search input */
  width?: string | { xs?: string; sm?: string; md?: string };
  /** Size of the input */
  size?: 'small' | 'medium';
  /** Debounce delay in milliseconds (default: 500ms) */
  debounceMs?: number;
  /** Enable auto-search while typing (default: true) */
  enableAutoSearch?: boolean;
}

/**
 * A reusable search input component that triggers search automatically while typing (debounced).
 * Isolates re-renders to this component only, preventing parent component lag.
 */
export const SearchInput: React.FC<SearchInputProps> = ({
  placeholder = 'Search...',
  tooltipText = 'Search automatically as you type',
  onSearch,
  searchValue = '',
  width = { xs: '50%', sm: '280px' },
  size = 'small',
  debounceMs = 500,
  enableAutoSearch = true
}) => {
  const theme = useTheme();
  const [localValue, setLocalValue] = useState(searchValue);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isInitialMount = useRef(true);
  
  // Use ref to store the latest onSearch callback to avoid dependency issues
  const onSearchRef = useRef(onSearch);
  useEffect(() => {
    onSearchRef.current = onSearch;
  }, [onSearch]);

  // Sync local value when external value changes (e.g., parent clears search)
  useEffect(() => {
    setLocalValue(searchValue);
  }, [searchValue]);

  // Debounced auto-search effect
  useEffect(() => {
    // Skip auto-search on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Skip if auto-search is disabled
    if (!enableAutoSearch) {
      return;
    }

    // Clear any existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer to trigger search after debounce delay
    debounceTimerRef.current = setTimeout(() => {
      onSearchRef.current(localValue);
    }, debounceMs);

    // Cleanup function
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [localValue, debounceMs, enableAutoSearch]);

  // Handle input change (isolated to this component)
  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setLocalValue(event.target.value);
  }, []);

  // Clear search and trigger empty search
  const handleClear = useCallback(() => {
    setLocalValue('');
    onSearchRef.current('');
  }, []);

  return (
    <Tooltip title={tooltipText} arrow>
      <TextField
        placeholder={placeholder}
        variant="outlined"
        size={size}
        value={localValue}
        onChange={handleChange}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search size={18} />
            </InputAdornment>
          ),
          endAdornment: localValue ? (
            <InputAdornment position="end">
              <Tooltip title="Clear search" arrow>
                <IconButton
                  size="small"
                  onClick={handleClear}
                  edge="end"
                  sx={{
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.error.main, 0.1),
                    }
                  }}
                >
                  <X size={16} />
                </IconButton>
              </Tooltip>
            </InputAdornment>
          ) : null,
          sx: {
            borderRadius: '8px',
            transition: theme.transitions.create(['box-shadow']),
            '&:focus-within': {
              boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
            },
          }
        }}
        sx={{ width }}
      />
    </Tooltip>
  );
};

